import {
  Alert,
  Button,
  CloseButton,
  Dialog,
  Portal,
  Stack,
  Text,
  createListCollection,
} from '@chakra-ui/react'
import { useFormik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { getActivitiesInRange } from '@/api/activity.api'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { parseInvoiceValidationError } from '@/helper/invoiceValidation.helpers'
import type { ActivityValidationIssue } from '@/helper/invoiceValidation.helpers'
import { translateApiErrorCode } from '@/helper/apiError.helpers'
import {
  createDraftInvoice,
  finalizeInvoice,
  sendInvoiceById,
  submitInvoice,
  getInvoiceById,
  updateDraftInvoice,
} from '@/api/invoices.api'

import {
  activityTypeToTitle,
  detectEditMode,
  lineTotalCents,
  periodToDateRange,
  toPeriod,
  translateInvoiceErrorMessage,
} from '../../helper/invoice.helpers'
import type { CreateInvoicePayload, InvoiceSelectableActivity } from './invoice.types'
import type {
  ActivityFilterTab,
  CreateInvoiceDialogProps,
  PreviewGroup,
  SubmitAction,
} from './CreateInvoiceDialog.types'
import { InvoiceDialogFormView, InvoiceDialogSentView } from './CreateInvoiceDialogForm'
import { resolveSubmissionPeriod, useNextInvoiceNumberPreview } from './invoiceNumberPreview'
import { SendInvoiceConfirmDialog } from './SendInvoiceConfirmDialog'
import { makeInvoiceValidationSchema } from './createInvoice.schema'
import {
  INVOICE_SETTINGS_ROUTE,
  INVOICE_SUBMIT_FAILED_TITLE,
  SELLER_TAX_DATA_TOAST,
  isSellerTaxDataError,
  showSellerTaxDataToast,
} from '@/helper/sellerTaxData.helpers'

function monthItemsDe() {
  return Array.from({ length: 12 }, (_, i) => ({
    label: new Date(0, i).toLocaleString('de-DE', { month: 'long' }),
    value: String(i + 1),
  }))
}

function toDateOnly(value: unknown): string {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

const UNION_ID = 1

function isUnionActivity(activity: any): boolean {
  return Number(activity?.bankId) === UNION_ID || Number(activity?.customerId) === UNION_ID
}

function mapActivityToSelectable(activity: any): InvoiceSelectableActivity {
  const union = isUnionActivity(activity)

  const rawType = activity?.serviceCode || activity?.serviceType || activity?.type

  const mappedType = union
    ? activityTypeToTitle(rawType)
    : activity?.serviceName ||
      activity?.serviceLabel ||
      activity?.typeLabel ||
      activity?.title ||
      'Aktivität'

  const amount = Number(activity?.timeQuantity ?? activity?.units ?? activity?.quantity ?? 1)

  const internalComment = activity?.notes || activity?.comment || activity?.description || ''

  return {
    id: Number(activity?.id ?? 0),
    date: toDateOnly(activity?.date) || '',
    title: mappedType, // keep for compatibility
    activityType: mappedType,
    amount,
    internalComment,
    subtitle: internalComment,
    customerId: Number(activity?.customerId ?? 0),
  }
}

export default function CreateInvoiceDialog({
  isOpen,
  onOpenChange,
  trigger,
  defaultBankName = 'Union Investment',
  defaultBankId = 1,
  autoInvoiceEnabled,
  autoInvoiceDayOfMonth,
  onGoToInvoices,
  onDownloadCreatedInvoice,
  mode = 'create',
  draftInvoice,
  draftInvoiceId,
  banks,
  onChanged,
  invoiceSettingsComplete,
  invoiceSettingsMissingFields,
  onGoToInvoiceSettings,
}: CreateInvoiceDialogProps) {
  const today = useMemo(() => new Date(Date.now()), [])
  const [submitting, setSubmitting] = useState<SubmitAction | null>(null)
  const [sentInvoiceId, setSentInvoiceId] = useState<string | null>(null)
  const [validationOpen, setValidationOpen] = useState(false)
  const [validationBlocking, setValidationBlocking] = useState<ActivityValidationIssue[]>([])
  const [validationWarnings, setValidationWarnings] = useState<ActivityValidationIssue[]>([])

  const [activities, setActivities] = useState<InvoiceSelectableActivity[]>([])
  const [activityFilterTab, setActivityFilterTab] = useState<ActivityFilterTab>('ALL')

  const canCreateOrSend = invoiceSettingsComplete !== false

  function showValidationDetails(e: unknown): boolean {
    const parsed = parseInvoiceValidationError(e)
    if (!parsed) return false

    setValidationBlocking(parsed.blocking)
    setValidationWarnings(parsed.warnings)
    setValidationOpen(true)

    toast.error(parsed.title, 'Bitte prüfen Sie die betroffenen Aktivitäten.')
    return true
  }

  const [sendConfirmOpen, setSendConfirmOpen] = useState(false)
  const [pendingSendInvoiceId, setPendingSendInvoiceId] = useState<string | null>(null)
  const [pendingSendInvoiceNumber, setPendingSendInvoiceNumber] = useState<string | null>(null)
  const [pendingSendContactName, setPendingSendContactName] = useState<string | null>(null)
  const [pendingSendContactEmail, setPendingSendContactEmail] = useState<string | null>(null)

  const monthCollection = useMemo(() => createListCollection({ items: monthItemsDe() }), [])
  const bankCollection = useMemo(
    () =>
      createListCollection({
        items:
          banks && banks.length > 0
            ? banks
            : [{ label: defaultBankName, value: String(defaultBankId) }],
      }),
    [banks, defaultBankName, defaultBankId]
  )

  const previewGroups = useMemo<PreviewGroup[]>(() => {
    if (mode !== 'edit' || !draftInvoice) return []

    const m = new Map<string, typeof draftInvoice.lines>()
    draftInvoice.lines.forEach(l => {
      const key = l.serviceGroup
      m.set(key, [...(m.get(key) ?? []), l])
    })

    return Array.from(m.entries()).map(([group, lines]) => {
      const subtotalCents = lines.reduce((acc, l) => acc + lineTotalCents(l), 0)
      return { group, lines, subtotalCents }
    })
  }, [mode, draftInvoice])

  const validationSchema = useMemo(() => makeInvoiceValidationSchema(), [])

  const formik = useFormik<CreateInvoicePayload>({
    validateOnBlur: true,
    validateOnChange: true,
    initialValues: {
      mode: 'MONTH',
      bankId: defaultBankId,
      useCustomInvoiceNumber: false,
      invoiceNumber: '',
      billingMonth: today.getMonth() + 1,
      billingYear: today.getFullYear(),
      periodStart: '',
      periodEnd: '',
      selectedActivityIds: [],
      comment: '',
    },
    validationSchema,
    onSubmit: async () => {},
  })

  const visibleActivities = useMemo(() => {
    switch (activityFilterTab) {
      case 'SELECTED':
        return activities.filter(a => formik.values.selectedActivityIds?.includes(a.id))
      case 'UNSELECTED':
        return activities.filter(a => !formik.values.selectedActivityIds?.includes(a.id))
      case 'ALL':
      default:
        return activities
    }
  }, [activities, activityFilterTab, formik.values.selectedActivityIds])

  const groupedVisibleActivities = useMemo(() => {
    const groups = new Map<string, InvoiceSelectableActivity[]>()

    visibleActivities.forEach(activity => {
      const key = activity.date
      groups.set(key, [...(groups.get(key) ?? []), activity])
    })

    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [visibleActivities])

  function isSelected(id: number) {
    return (formik.values.selectedActivityIds ?? []).includes(id)
  }

  function toggleActivity(id: number) {
    const current = formik.values.selectedActivityIds ?? []
    const next = current.includes(id) ? current.filter(x => x !== id) : [...current, id]

    formik.setFieldValue('selectedActivityIds', next)
  }
  function getRequestedPeriod() {
    if (formik.values.mode === 'MONTH') {
      return toPeriod(Number(formik.values.billingYear), Number(formik.values.billingMonth))
    }

    if (formik.values.mode === 'DATE_RANGE') {
      return {
        periodStart: String(formik.values.periodStart ?? ''),
        periodEnd: String(formik.values.periodEnd ?? ''),
      }
    }

    // Aktivitäten mode:
    // load a broad sensible range when there is no explicit range selector
    const year = Number(formik.values.billingYear || today.getFullYear())
    return {
      periodStart: `${year}-01-01`,
      periodEnd: `${year}-12-31`,
    }
  }

  useEffect(() => {
    if (!isOpen) return

    setSentInvoiceId(null)
    setActivityFilterTab('ALL')

    if (mode === 'edit' && draftInvoice) {
      const periodStart = draftInvoice.periodStart || draftInvoice.periodFrom || ''
      const periodEnd = draftInvoice.periodEnd || draftInvoice.periodTo || ''
      const start = periodStart ? new Date(periodStart) : today

      const detectedMode = detectEditMode(draftInvoice)

      formik.setValues({
        mode: detectedMode,
        bankId: draftInvoice.bankId,
        useCustomInvoiceNumber: false,
        invoiceNumber: '',
        billingMonth: start.getMonth() + 1,
        billingYear: start.getFullYear(),
        periodStart,
        periodEnd,
        selectedActivityIds: draftInvoice.lines.map(l => l.entityId),
        comment: draftInvoice.comment ?? '',
      })
    } else {
      formik.resetForm({
        values: {
          mode: 'MONTH',
          bankId: defaultBankId,
          useCustomInvoiceNumber: false,
          invoiceNumber: '',
          billingMonth: today.getMonth() + 1,
          billingYear: today.getFullYear(),
          periodStart: '',
          periodEnd: '',
          selectedActivityIds: [],
          comment: '',
        },
      })
    }
  }, [isOpen, mode, draftInvoice, defaultBankId, today]) // eslint-disable-line react-hooks/exhaustive-deps
  const currentDraftActivityIds = useMemo(
    () => new Set((draftInvoice?.lines ?? []).map(l => Number(l.entityId))),
    [draftInvoice]
  )
  useEffect(() => {
    let mounted = true

    async function loadActivities() {
      if (!isOpen) return

      const bankId = Number(formik.values.bankId)
      if (!bankId) {
        if (mounted) setActivities([])
        return
      }

      const { periodStart, periodEnd } = getRequestedPeriod()
      if (!periodStart || !periodEnd) {
        if (mounted) setActivities([])
        return
      }

      try {
        const { from, to } = periodToDateRange(periodStart, periodEnd)
        const res = await getActivitiesInRange(from, to)

        const items = res?.items ?? []

        console.log('invoice dialog raw activities', items)
        console.log('selected bankId', bankId)
        console.log('requested period', { periodStart, periodEnd })

        const filtered = items
          .filter((activity: any) => Boolean(activity) && Number(activity.id) > 0)
          .filter((activity: any) => {
            const id = Number(activity.id)
            const alreadyInThisInvoice = currentDraftActivityIds.has(id)
            return activity.invoiced === false || alreadyInThisInvoice
          })
          .filter((activity: any) => {
            const activityBankId = Number(activity.bankId ?? 0)
            const activityCustomerId = Number(activity.customerId ?? 0)

            return activityBankId === bankId || activityCustomerId === bankId
          })
          .map(mapActivityToSelectable)
          .sort((a, b) => {
            const dateA = String(a.date ?? '')
            const dateB = String(b.date ?? '')
            if (dateA !== dateB) return dateA.localeCompare(dateB)

            const labelA = String(a.activityType ?? a.title ?? '')
            const labelB = String(b.activityType ?? b.title ?? '')
            return labelA.localeCompare(labelB, 'de')
          })

        console.log('invoice dialog filtered activities', filtered)

        if (!mounted) return

        setActivities(filtered)

        const allowedIds = new Set(filtered.map(a => a.id))
        const current = formik.values.selectedActivityIds ?? []
        const next = current.filter(id => allowedIds.has(id) || currentDraftActivityIds.has(id))

        if (next.length !== current.length) {
          formik.setFieldValue('selectedActivityIds', next, false)
        }
      } catch (e: any) {
        if (!mounted) return
        setActivities([])

        toast.error(
          'Aktivitäten konnten nicht geladen werden',
          translateApiErrorCode(e?.code) ??
            'Beim Abrufen der Aktivitäten ist ein unerwarteter Fehler aufgetreten.'
        )
      }
    }

    loadActivities()

    return () => {
      mounted = false
    }
  }, [
    isOpen,
    formik.values.mode,
    formik.values.bankId,
    formik.values.billingMonth,
    formik.values.billingYear,
    formik.values.periodStart,
    formik.values.periodEnd,
  ])

  const commentLen = (formik.values.comment ?? '').length

  // The period the draft will be created with. Shared with the invoice-number preview so the
  // previewed number is computed for exactly the `periodStart` we submit (SIB-944).
  const submissionPeriod = useMemo(
    () => resolveSubmissionPeriod(formik.values, activities),
    [formik.values, activities]
  )

  const invoiceNumberPreview = useNextInvoiceNumberPreview({
    enabled:
      !!isOpen &&
      mode === 'create' &&
      !!autoInvoiceEnabled &&
      !formik.values.useCustomInvoiceNumber,
    periodStart: submissionPeriod.periodStart,
  })

  function buildSubmissionData() {
    const { periodStart, periodEnd } = submissionPeriod
    let itemsIds: number[] = []

    if (formik.values.mode === 'MONTH' || formik.values.mode === 'DATE_RANGE') {
      // in month / range mode use all currently loaded activities for that bank and period
      itemsIds = activities.map(a => a.id)
    }

    if (formik.values.mode === 'ACTIVITY_SELECTION') {
      itemsIds = formik.values.selectedActivityIds ?? []

      if (!itemsIds.length) {
        throw new Error('NO_SELECTED_ACTIVITIES')
      }

      if (!periodStart || !periodEnd) {
        throw new Error('NO_VALID_SELECTED_ACTIVITIES')
      }
    }

    return { periodStart, periodEnd, itemsIds }
  }

  async function run(action: SubmitAction) {
    const errors = await formik.validateForm()
    const isUnionFlow = Number(formik.values.bankId) === UNION_ID

    if (Object.keys(errors).length > 0) {
      formik.setTouched(
        {
          mode: true,
          bankId: true,
          billingMonth: true,
          billingYear: true,
          periodStart: true,
          periodEnd: true,
          invoiceNumber: true,
          selectedActivityIds: true,
          comment: true,
        },
        true
      )
      return
    }

    setSubmitting(action)

    // Which step failed decides the toast title: creating/finalising the invoice, or handing an
    // existing draft in for approval.
    let failedStep: 'create' | 'submit' = 'create'

    try {
      const bankId = Number(formik.values.bankId)
      const { periodStart, periodEnd, itemsIds } = buildSubmissionData()

      const editId = draftInvoice?.id ?? draftInvoiceId ?? null
      const isEdit = mode === 'edit' && !!editId

      let invoiceId = ''

      if (!isEdit) {
        const draft = await createDraftInvoice({
          scope: 'ACTIVITY',
          bankId,
          periodStart,
          periodEnd,
          itemsIds,
          comments: (formik.values.comment ?? '').trim() || undefined,
          invoiceNumber:
            formik.values.useCustomInvoiceNumber && formik.values.invoiceNumber?.trim()
              ? formik.values.invoiceNumber.trim()
              : undefined,
        })

        invoiceId = draft.id
      } else {
        invoiceId = String(editId)

        const customerId = draftInvoice?.lines?.length
          ? Number(draftInvoice.lines[0].customerId ?? 0)
          : undefined

        await updateDraftInvoice(invoiceId, {
          customerId,
          periodStart,
          periodEnd,
          itemsIds,
          comments: (formik.values.comment ?? '').trim() || undefined,
          invoiceNumber:
            formik.values.useCustomInvoiceNumber && formik.values.invoiceNumber?.trim()
              ? formik.values.invoiceNumber.trim()
              : undefined,
        })
      }

      if (action === 'preview') {
        await onChanged?.(invoiceId)
        onOpenChange?.(false)
        return
      }

      if (isUnionFlow) {
        if (action !== 'submit') return

        failedStep = 'submit'
        const submitted = await submitInvoice(invoiceId)
        await onChanged?.(submitted.id)
        onOpenChange?.(false)
        toast.success('Rechnung wurde zur Freigabe eingereicht.')
        return
      }

      const finalized = await finalizeInvoice(invoiceId)

      if (action === 'create') {
        await onChanged?.(finalized.id)
        onOpenChange?.(false)
        return
      }

      if (action === 'create_and_send') {
        const finalizedInvoice = await getInvoiceById(finalized.id)

        setPendingSendInvoiceId(finalized.id)
        setPendingSendInvoiceNumber(finalizedInvoice.invoiceNumber ?? null)
        setPendingSendContactName(finalizedInvoice.contactDetails?.contactName ?? null)
        setPendingSendContactEmail(finalizedInvoice.contactDetails?.contactEmail ?? null)
        setSendConfirmOpen(true)
        return
      }
    } catch (e: any) {
      // The user's own tax data blocks the invoice — nothing here can fix that, so say so and
      // offer the way to the settings instead of the generic "check the invoice data".
      if (isSellerTaxDataError(e)) {
        showSellerTaxDataToast(
          () => {
            onOpenChange?.(false)
            if (onGoToInvoiceSettings) onGoToInvoiceSettings()
            else window.location.assign(INVOICE_SETTINGS_ROUTE)
          },
          failedStep === 'submit' ? INVOICE_SUBMIT_FAILED_TITLE : SELLER_TAX_DATA_TOAST.title,
          e
        )
        return
      }

      if (showValidationDetails(e)) return

      const msg = typeof e?.message === 'string' ? e.message : String(e ?? '')

      if (msg === 'NO_SELECTED_ACTIVITIES') {
        toast.info(
          'Keine Aktivitäten ausgewählt',
          'Bitte wählen Sie mindestens eine Aktivität aus der Liste aus.'
        )
        return
      }

      if (msg === 'NO_VALID_SELECTED_ACTIVITIES') {
        toast.info(
          'Keine gültigen Aktivitäten ausgewählt',
          'Bitte laden Sie die Liste neu und wählen Sie die Aktivitäten erneut aus.'
        )
        return
      }

      if (msg.includes('No activities found')) {
        toast.info(
          'Keine Aktivitäten gefunden',
          'Für den ausgewählten Zeitraum und Auftraggeber gibt es keine Aktivitäten. Bitte wählen Sie einen anderen Auftraggeber oder Zeitraum.'
        )
        return
      }

      if (e?.code === 'CONFLICT') {
        toast.error(
          'Rechnungsnummer bereits vergeben',
          'Eine Rechnung mit dieser Nummer existiert bereits. Bitte verwenden Sie eine andere Nummer.'
        )
        return
      }

      toast.error(
        'Rechnung konnte nicht erstellt werden',
        translateApiErrorCode(e?.code) ??
          translateInvoiceErrorMessage(msg) ??
          'Bitte prüfen Sie die Rechnungsdaten und versuchen Sie es erneut.'
      )
    } finally {
      setSubmitting(null)
    }
  }

  async function confirmAndSendPendingInvoice() {
    if (!pendingSendInvoiceId) return

    try {
      setSubmitting('create_and_send')

      const sent = await sendInvoiceById(pendingSendInvoiceId)
      setSendConfirmOpen(false)
      setPendingSendInvoiceId(null)
      setPendingSendInvoiceNumber(null)
      setPendingSendContactName(null)
      setPendingSendContactEmail(null)

      setSentInvoiceId(sent.id)
      await onChanged?.(sent.id)
    } catch (e: any) {
      const msg = typeof e?.message === 'string' ? e.message : 'Unbekannter Fehler'
      toast.error(
        'Rechnung konnte nicht versandt werden',
        translateApiErrorCode(e?.code) ??
          translateInvoiceErrorMessage(msg) ??
          'Bitte führen Sie den Versand erneut aus.'
      )
    } finally {
      setSubmitting(null)
    }
  }
  const hasActivitiesInCurrentSelection =
    formik.values.mode === 'ACTIVITY_SELECTION'
      ? (formik.values.selectedActivityIds?.length ?? 0) > 0
      : activities.length > 0

  const canSubmit =
    canCreateOrSend &&
    !!formik.values.bankId &&
    ((formik.values.mode === 'MONTH' &&
      !!formik.values.billingMonth &&
      !!formik.values.billingYear) ||
      (formik.values.mode === 'DATE_RANGE' &&
        !!formik.values.periodStart &&
        !!formik.values.periodEnd) ||
      (formik.values.mode === 'ACTIVITY_SELECTION' &&
        (formik.values.selectedActivityIds?.length ?? 0) > 0)) &&
    hasActivitiesInCurrentSelection

  const close = () => onOpenChange?.(false)

  return (
    <Dialog.Root
      modal
      open={isOpen}
      onOpenChange={e => onOpenChange?.(e.open)}
      size="xl"
      scrollBehavior="inside"
    >
      <Portal>
        {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
        <Dialog.Backdrop />
        <Dialog.Positioner zIndex="modal">
          <Dialog.Content>
            {sentInvoiceId ? (
              <InvoiceDialogSentView
                mode={mode}
                sentInvoiceId={sentInvoiceId}
                onClose={close}
                onGoToInvoices={onGoToInvoices}
                onDownloadCreatedInvoice={onDownloadCreatedInvoice}
              />
            ) : (
              <InvoiceDialogFormView
                mode={mode}
                draftInvoice={draftInvoice}
                previewGroups={previewGroups}
                formik={formik}
                commentLen={commentLen}
                monthCollection={monthCollection}
                bankCollection={bankCollection}
                submitting={submitting}
                canCreateOrSend={canCreateOrSend}
                autoInvoiceEnabled={autoInvoiceEnabled}
                autoInvoiceDayOfMonth={autoInvoiceDayOfMonth}
                invoiceNumberPreview={invoiceNumberPreview}
                invoiceSettingsComplete={invoiceSettingsComplete}
                invoiceSettingsMissingFields={invoiceSettingsMissingFields}
                onGoToInvoiceSettings={onGoToInvoiceSettings}
                onClose={close}
                activities={activities}
                visibleActivities={visibleActivities}
                groupedVisibleActivities={groupedVisibleActivities}
                activityFilterTab={activityFilterTab}
                setActivityFilterTab={setActivityFilterTab}
                isSelected={isSelected}
                toggleActivity={toggleActivity}
                canSubmit={canSubmit}
                run={run}
              />
            )}
          </Dialog.Content>
        </Dialog.Positioner>

        <Dialog.Root open={validationOpen} onOpenChange={e => setValidationOpen(e.open)}>
          <Portal>
            <Dialog.Backdrop />
            <Dialog.Positioner zIndex="modal">
              <Dialog.Content
                maxW={{ base: '90vw', md: '2xl' }}
                maxH="70dvh"
                overflow="hidden"
                display="flex"
                flexDirection="column"
              >
                <Dialog.Header>
                  <Dialog.Title>Rechnungsdaten prüfen</Dialog.Title>

                  <Dialog.CloseTrigger asChild>
                    <CloseButton />
                  </Dialog.CloseTrigger>
                </Dialog.Header>

                <Dialog.Body overflow="auto">
                  <Stack gap={4} py={4}>
                    {validationBlocking.length > 0 && (
                      <Alert.Root status="error" variant="surface">
                        <Alert.Indicator />
                        <Alert.Content>
                          <Alert.Title>Rechnung kann nicht erstellt werden</Alert.Title>
                          <Alert.Description>
                            <Stack gap={1} mt={2}>
                              {validationBlocking.map((it, idx) => (
                                <Text key={`${it.activityId ?? 'x'}-${idx}`}>• {it.message}</Text>
                              ))}
                            </Stack>
                          </Alert.Description>
                        </Alert.Content>
                      </Alert.Root>
                    )}

                    {validationWarnings.length > 0 && (
                      <Alert.Root status="warning" variant="surface">
                        <Alert.Indicator />
                        <Alert.Content>
                          <Alert.Title>Bitte prüfen Sie folgende Hinweise</Alert.Title>
                          <Alert.Description>
                            <Stack gap={1} mt={2}>
                              {validationWarnings.map((it, idx) => (
                                <Text key={`${it.activityId ?? 'x'}-${idx}`}>• {it.message}</Text>
                              ))}
                            </Stack>
                            <Text textStyle="micro" color="fg.muted" mt={2}>
                              Eine fehlende Bestellreferenz verhindert die Rechnungserstellung
                              derzeit nicht. Bitte prüfen Sie die Zuordnung dennoch sorgfältig.
                            </Text>
                          </Alert.Description>
                        </Alert.Content>
                      </Alert.Root>
                    )}
                  </Stack>
                </Dialog.Body>

                <Dialog.Footer>
                  <Button variant="solid" onClick={() => setValidationOpen(false)}>
                    Schließen
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>
        <SendInvoiceConfirmDialog
          isOpen={sendConfirmOpen}
          onClose={() => {
            if (submitting !== 'create_and_send') {
              setSendConfirmOpen(false)
            }
          }}
          isSending={submitting === 'create_and_send'}
          invoiceNumber={pendingSendInvoiceNumber}
          contactName={pendingSendContactName}
          contactEmail={pendingSendContactEmail}
          onConfirm={confirmAndSendPendingInvoice}
        />
      </Portal>
    </Dialog.Root>
  )
}
